import { useState, useEffect, useCallback, useRef } from 'react';
import { admin } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const ROLES = [
  { value: 'ceo',               label: 'CEO',               group: 'Executive' },
  { value: 'cfo',               label: 'CFO',               group: 'Executive' },
  { value: 'cto',               label: 'CTO',               group: 'Executive' },
  { value: 'hr_head',           label: 'HR Head',           group: 'HR' },
  { value: 'hr_executive',      label: 'HR Executive',      group: 'HR' },
  { value: 'it_admin',          label: 'IT Admin',          group: 'IT' },
  { value: 'it_support',        label: 'IT Support',        group: 'IT' },
  { value: 'finance_executive', label: 'Finance Executive', group: 'Finance' },
  { value: 'project_manager',    label: 'Project Manager',    group: 'Projects' },
  { value: 'project_lead',       label: 'Project Lead',       group: 'Projects' },
  { value: 'team_lead',          label: 'Team Lead',          group: 'Projects' },
  { value: 'solution_architect', label: 'Solution Architect', group: 'Projects' },
  { value: 'dev_lead',           label: 'Dev Lead',           group: 'Projects' },
  { value: 'developer',          label: 'Developer',          group: 'Projects' },
  { value: 'qa_lead',            label: 'QA Lead',            group: 'Projects' },
  { value: 'tester',             label: 'Tester',             group: 'Projects' },
  { value: 'business_analyst',   label: 'Business Analyst',   group: 'Projects' },
  { value: 'sales_head',        label: 'Sales Head',        group: 'Sales' },
  { value: 'sales_executive',   label: 'Sales Executive',   group: 'Sales' },
  { value: 'office_admin',      label: 'Office Admin',      group: 'Office' },
  { value: 'employee',          label: 'Employee',          group: 'General' },
  { value: 'super_admin',       label: 'Super Admin',       group: 'Admin' },
  // Legacy aliases kept for existing users
  { value: 'hr_admin',          label: 'HR Admin (legacy)',          group: 'Legacy' },
  { value: 'finance_team',      label: 'Finance Team (legacy)',      group: 'Legacy' },
  { value: 'finance_head',      label: 'Finance Head (legacy)',      group: 'Legacy' },
  { value: 'reporting_manager', label: 'Reporting Manager (legacy)', group: 'Legacy' },
];

const ROLE_LABEL_MAP = Object.fromEntries(ROLES.map(r => [r.value, r.label]));

// ─── Searchable Role Dropdown ─────────────────────────────────────────────────
function RoleSelect({ value, onChange, style }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = ROLES.filter(r =>
    !q || r.label.toLowerCase().includes(q.toLowerCase()) || r.group.toLowerCase().includes(q.toLowerCase())
  );

  const groups = [...new Set(filtered.map(r => r.group))];
  const selectedLabel = ROLE_LABEL_MAP[value] || value;

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <div
        onClick={() => { setOpen(v => !v); setQ(''); }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '0.6rem 0.875rem', border: `1.5px solid ${open ? theme.primary : theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: '#fff', userSelect: 'none' }}
      >
        <span style={{ fontWeight: 500 }}>{selectedLabel}</span>
        <span style={{ color: '#9CA3AF', fontSize: '0.7rem' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 999, maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '0.5rem' }}>
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search roles…"
              style={{ width: '100%', padding: '0.45rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.375rem', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {groups.map(group => (
              <div key={group}>
                <div style={{ padding: '0.3rem 0.875rem', fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', background: '#F9FAFB' }}>
                  {group}
                </div>
                {filtered.filter(r => r.group === group).map(r => (
                  <div
                    key={r.value}
                    onClick={() => { onChange(r.value); setOpen(false); setQ(''); }}
                    style={{ padding: '0.55rem 0.875rem 0.55rem 1.25rem', cursor: 'pointer', fontSize: '0.875rem', color: r.value === value ? theme.primary : theme.text, fontWeight: r.value === value ? 600 : 400, background: r.value === value ? theme.primary + '10' : 'transparent' }}
                    onMouseEnter={e => { if (r.value !== value) e.currentTarget.style.background = '#F3F4F6'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = r.value === value ? theme.primary + '10' : 'transparent'; }}
                  >
                    {r.value === value && <span style={{ marginRight: '0.4rem' }}>✓</span>}
                    {r.label}
                  </div>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.875rem' }}>No roles found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', full_name: '', role: 'employee', password: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await admin.getUsers();
      setUsers(r.data || []);
    } catch { setUsers([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.username) { setError('Username is required.'); return; }
    if (!modal?.id && !form.password) { setError('Password is required for new users.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (modal?.id && !payload.password) delete payload.password;
      if (modal?.id) {
        await admin.updateUser(modal.id, payload);
      } else {
        await admin.createUser(payload);
      }
      setModal(null); load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save user.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This action cannot be undone.')) return;
    try {
      await admin.deleteUser(id); load();
    } catch { alert('Failed to delete user.'); }
  };

  const columns = [
    {
      header: 'User', key: 'username',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: theme.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: theme.primaryDark }}>
            {(row.full_name || v).charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>{row.full_name || v}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted }}>{v}</p>
          </div>
        </div>
      ),
    },
    { header: 'Email', key: 'email', render: (v) => v || '—' },
    { header: 'Role', key: 'role', render: (v) => <Badge status={v} label={ROLE_LABEL_MAP[v] || v} /> },
    { header: 'Status', key: 'is_active', render: (v) => <Badge status={v ? 'active' : 'inactive'} /> },
    { header: 'Created', key: 'created_at', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => { setModal(row); setForm({ username: row.username, email: row.email || '', full_name: row.full_name || '', role: row.role || 'employee', password: '', is_active: row.is_active !== false }); setError(''); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(id)}>Delete</Button>
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || (u.full_name || '').toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    const matchRole = !filterRole || u.role === filterRole;
    const matchStatus = filterStatus === '' || (filterStatus === 'active' ? u.is_active !== false : u.is_active === false);
    return matchSearch && matchRole && matchStatus;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>User Management</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {filteredUsers.length} of {users.length} users
          </p>
        </div>
        <Button variant="primary" onClick={() => { setModal({}); setForm({ username: '', email: '', full_name: '', role: 'employee', password: '', is_active: true }); setError(''); }}>+ Add User</Button>
      </div>

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '200px' }}>
          <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '0.9rem', pointerEvents: 'none' }}>🔍</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, username or email…"
            style={{ width: '100%', padding: '0.6rem 0.875rem 0.6rem 2.25rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
          />
        </div>
        <select
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
          style={{ padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', background: '#fff', minWidth: '160px' }}
        >
          <option value="">All Roles</option>
          {ROLES.filter(r => !r.group.includes('Legacy')).map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', background: '#fff', minWidth: '130px' }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {(search || filterRole || filterStatus) && (
          <button onClick={() => { setSearch(''); setFilterRole(''); setFilterStatus(''); }}
            style={{ padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.8rem', color: '#6B7280', background: '#fff', cursor: 'pointer', fontWeight: 500 }}>
            Clear
          </button>
        )}
      </div>

      <Card padding="0">
        <Table columns={columns} data={filteredUsers} loading={loading} emptyMessage="No users found" />
      </Card>

      <Modal isOpen={!!modal} onClose={() => { setModal(null); setError(''); }} title={modal?.id ? 'Edit User' : 'Create New User'} maxWidth="540px"
        footer={<><Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>{modal?.id ? 'Update' : 'Create User'}</Button></>}>
        {error && (
          <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.625rem 0.875rem', marginBottom: '1rem', color: theme.danger, fontSize: '0.8rem' }}>
            ⚠️ {error}
          </div>
        )}
        <label style={lbl}>Full Name</label>
        <input style={inp} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" />
        <label style={lbl}>Username *</label>
        <input style={inp} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="username" />
        <label style={lbl}>Email</label>
        <input style={inp} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@bonton.com" />
        <label style={lbl}>Password {modal?.id ? '(leave blank to keep current)' : '*'}</label>
        <input style={inp} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={modal?.id ? 'Leave blank to keep unchanged' : 'Enter password'} />
        <label style={lbl}>Role *</label>
        <RoleSelect value={form.role} onChange={(r) => setForm({ ...form, role: r })} style={{ marginBottom: '0.75rem' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="is_active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          <label htmlFor="is_active" style={{ fontSize: '0.875rem', color: theme.text }}>Active user</label>
        </div>
      </Modal>
    </div>
  );
}

export default UserManagement;
